{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "wealthlane://modules/participant",
  "title": "Participant",
  "description": "Equity plan participant — personal info, employment, addresses, compliance, transfer agent.",
  "type": "object",
  "$defs": {
    "addressLine1": {
      "type": "string",
      "title": "Address Line 1",
      "maxLength": 100,
      "noBlank": true,
      "noDoubleSpace": true
    },
    "addressLine2": {
      "type": "string",
      "title": "Address Line 2",
      "maxLength": 100,
      "noBlank": true,
      "noDoubleSpace": true
    },
    "addressCountryId": {
      "type": "string",
      "title": "Country",
      "format": "uuid",
      "widget": "select",
      "actionId": "FETCH_COUNTRIES"
    },
    "addressZipCode": {
      "type": "string",
      "title": "ZIP/Postal Code",
      "maxLength": 20
    },
    "addressStateId": {
      "type": "string",
      "title": "State/Province/Region",
      "format": "uuid",
      "widget": "select",
      "actionId": "FETCH_STATES"
    },
    "addressStateText": {
      "type": "string",
      "title": "State/Province/Region",
      "maxLength": 50
    },
    "addressCityText": {
      "type": "string",
      "title": "City",
      "maxLength": 60,
      "noBlank": true,
      "noDoubleSpace": true
    },
    "email": {
      "type": "string",
      "title": "Email",
      "format": "email",
      "maxLength": 100,
      "pattern": "^[^@]+@[^@]+\\.[^@]+$"
    },
    "jobTitle": {
      "type": "string",
      "title": "Job Title"
    },
    "costCenter": {
      "type": "string",
      "title": "Cost Center"
    },
    "periodStartDate": {
      "type": "string",
      "title": "Start Date",
      "formatKey": "DATE_MDY"
    },
    "periodEndDate": {
      "type": "string",
      "title": "End Date",
      "formatKey": "DATE_MDY"
    },
    "participantId": {
      "type": "string",
      "title": "Participant Name",
      "format": "uuid",
      "widget": "search-picker",
      "columns": [
        "employeeId",
        "firstName",
        "lastName"
      ],
      "subtitleField": "employeeId",
      "subtitlePrefix": "ID:"
    }
  },
  "properties": {
    "payrollId": {
      "type": "string",
      "title": "Payroll ID",
      "maxLength": 100,
      "noBlank": true
    },
    "employeeId": {
      "type": "string",
      "title": "Employee ID",
      "pattern": "^[a-zA-Z0-9_\\-/ ]+$",
      "maxLength": 50
    },
    "employeeTypeId": {
      "type": "string",
      "title": "Employee Type",
      "format": "uuid",
      "widget": "select",
      "actionId": "FETCH_EMPLOYEE_TYPES"
    },
    "firstName": {
      "type": "string",
      "title": "First Name",
      "maxLength": 100,
      "noBlank": true,
      "noDoubleSpace": true,
      "charset": "nameChars"
    },
    "middleName": {
      "type": "string",
      "title": "Middle Name",
      "maxLength": 100,
      "noBlank": true,
      "noDoubleSpace": true,
      "charset": "nameChars"
    },
    "lastName": {
      "type": "string",
      "title": "Last Name",
      "maxLength": 100,
      "noBlank": true,
      "noDoubleSpace": true,
      "charset": "nameChars"
    },
    "dateOfBirth": {
      "type": "string",
      "title": "Date of Birth",
      "format": "date",
      "maxDate": "today",
      "masked": true,
      "maskedPlaceholder": "xx/xx/xxxx",
      "unmaskActionId": "UNMASK_DATE_OF_BIRTH",
      "maskedFormatKey": "DATE_MDY"
    },
    "workNumberCountryCodeId": {
      "type": "string",
      "title": "Work Code",
      "format": "uuid",
      "widget": "select",
      "actionId": "FETCH_COUNTRY_CODES"
    },
    "workNumber": {
      "type": "string",
      "title": "Work Number",
      "maxLength": 20,
      "inputFormatter": "DIGITS_ONLY"
    },
    "email": {
      "$ref": "#/$defs/email",
      "title": "Work Email"
    },
    "residentStatusId": {
      "type": "string",
      "title": "Residence Status",
      "format": "uuid",
      "widget": "select",
      "actionId": "FETCH_RESIDENT_STATUSES"
    },
    "residentStatusName": {
      "type": "string",
      "title": "Residence Status Name"
    },
    "countryId": {
      "$ref": "#/$defs/addressCountryId",
      "title": "Country of Legal Residency"
    },
    "ssn": {
      "type": "string",
      "title": "SSN",
      "widget": "password",
      "formatKey": "SSN",
      "inputFormatter": "SSN_US",
      "masked": true,
      "maskedPlaceholder": "xxx-xx-xxxx",
      "unmaskActionId": "UNMASK_SSN",
      "maskedFormatKey": "SSN"
    },
    "divisionId": {
      "type": "string",
      "title": "Division/Department",
      "format": "uuid",
      "widget": "select",
      "actionId": "FETCH_DIVISIONS"
    },
    "titleId": {
      "$ref": "#/$defs/jobTitle",
      "format": "uuid",
      "widget": "select",
      "actionId": "FETCH_JOB_TITLES"
    },
    "costCenter": {
      "$ref": "#/$defs/costCenter",
      "maxLength": 100,
      "noBlank": true
    },
    "hireDate": {
      "type": "string",
      "title": "Hire Date",
      "format": "date",
      "maxDate": "today"
    },
    "isSection16": {
      "type": "boolean",
      "title": "Is Section 16",
      "widget": "radio",
      "options": [
        {
          "label": "Yes",
          "value": true
        },
        {
          "label": "No",
          "value": false
        }
      ]
    },
    "isRule144": {
      "type": "boolean",
      "title": "Is Rule 144",
      "widget": "radio",
      "options": [
        {
          "label": "Yes",
          "value": true
        },
        {
          "label": "No",
          "value": false
        }
      ]
    },
    "settlementTypeId": {
      "$ref": "module://settlement#/$defs/settlementTypeId"
    },
    "employmentAddressLine1": {
      "$ref": "#/$defs/addressLine1"
    },
    "employmentAddressLine2": {
      "$ref": "#/$defs/addressLine2"
    },
    "employmentCountryId": {
      "$ref": "#/$defs/addressCountryId"
    },
    "employmentZipCode": {
      "$ref": "#/$defs/addressZipCode"
    },
    "employmentStateId": {
      "$ref": "#/$defs/addressStateId"
    },
    "employmentStateText": {
      "$ref": "#/$defs/addressStateText"
    },
    "employmentCityText": {
      "$ref": "#/$defs/addressCityText"
    },
    "isOtherCountry": {
      "type": "boolean",
      "default": false
    },
    "homeAddressLine1": {
      "$ref": "#/$defs/addressLine1"
    },
    "homeAddressLine2": {
      "$ref": "#/$defs/addressLine2"
    },
    "homeCountryId": {
      "$ref": "#/$defs/addressCountryId"
    },
    "homeZipCode": {
      "$ref": "#/$defs/addressZipCode"
    },
    "homeStateId": {
      "$ref": "#/$defs/addressStateId"
    },
    "homeStateText": {
      "$ref": "#/$defs/addressStateText"
    },
    "homeCityText": {
      "$ref": "#/$defs/addressCityText"
    },
    "isHomeOtherCountry": {
      "type": "boolean",
      "default": false
    },
    "isMailingAddressDifferent": {
      "type": "boolean",
      "title": "I have a different mailing address",
      "default": false,
      "widget": "checkbox"
    },
    "mailingAddressLine1": {
      "$ref": "#/$defs/addressLine1"
    },
    "mailingAddressLine2": {
      "$ref": "#/$defs/addressLine2"
    },
    "mailingCountryId": {
      "$ref": "#/$defs/addressCountryId"
    },
    "mailingZipCode": {
      "$ref": "#/$defs/addressZipCode"
    },
    "mailingStateId": {
      "$ref": "#/$defs/addressStateId"
    },
    "mailingStateText": {
      "$ref": "#/$defs/addressStateText"
    },
    "mailingCityText": {
      "$ref": "#/$defs/addressCityText"
    },
    "isMailingOtherCountry": {
      "type": "boolean",
      "default": false
    },
    "transferAgentCompanyId": {
      "type": "string",
      "title": "TA Name",
      "format": "uuid",
      "widget": "select",
      "actionId": "FETCH_TRANSFER_AGENTS"
    },
    "taAccountNumber": {
      "type": "string",
      "title": "TA Account Number",
      "maxLength": 100,
      "noBlank": true
    },
    "taName": {
      "type": "string",
      "title": "Transfer Agent Name"
    },
    "isOtherCountryPhone": {
      "type": "boolean",
      "default": false
    },
    "participantOtherCountry": {
      "type": "boolean",
      "default": false
    },
    "employeeStatus": {
      "type": "string",
      "title": "Employee Status",
      "default": "Active"
    },
    "settlementConfiguration": {
      "$ref": "module://settlement#/$defs/settlementConfiguration"
    },
    "employeeID": {
      "type": "string",
      "title": "Employee ID"
    },
    "employeeType": {
      "type": "string",
      "title": "Employee Type"
    },
    "tradePreclearanceRequired": {
      "type": "boolean",
      "title": "Trade Preclearance Required",
      "displayFormat": "boolean"
    },
    "residenceStatus": {
      "type": "string",
      "title": "Residence Status"
    },
    "issuingCountry": {
      "type": "string",
      "title": "Issuing Country"
    },
    "taxCertification": {
      "type": "string",
      "title": "Tax Certification",
      "displayFormat": "status",
      "statusMap": {
        "Certified": {
          "icon": "check_circle",
          "tone": "success"
        },
        "_default": {
          "icon": "cancel",
          "tone": "error"
        }
      }
    },
    "workState": {
      "type": "string",
      "title": "Work State"
    },
    "residentState": {
      "type": "string",
      "title": "Resident State"
    },
    "settlementType": {
      "type": "string",
      "title": "Settlement Type"
    },
    "ficaEligible": {
      "type": "boolean",
      "title": "FICA Eligible",
      "displayFormat": "status",
      "statusMap": {
        "true": {
          "icon": "check_circle",
          "tone": "success",
          "label": "Eligible"
        },
        "_default": {
          "icon": "cancel",
          "tone": "error",
          "label": "Not Eligible"
        }
      }
    },
    "hasTaxRates": {
      "type": "boolean"
    },
    "isTaxCertified": {
      "type": "boolean"
    },
    "viewTaxCertificateAction": {
      "type": "string",
      "title": "View Tax Certificate",
      "actionId": "VIEW_TAX_CERTIFICATE_PARTICIPANT"
    },
    "ytdWages": {
      "type": "number",
      "title": "YTD Wages",
      "formatKey": "CURRENCY"
    },
    "ytdSupplementalIncome": {
      "type": "number",
      "title": "YTD Supplemental Income",
      "formatKey": "CURRENCY"
    },
    "taxName": {
      "type": "string",
      "title": "Tax Name"
    },
    "taxRate": {
      "type": "number",
      "title": "Rate",
      "emptyText": "-"
    },
    "accountsCompanyName": {
      "type": "string",
      "title": "Company Name",
      "emptyText": "-"
    },
    "accountNumner": {
      "type": "string",
      "title": "Account Number",
      "emptyText": "-"
    },
    "accountStatus": {
      "type": "string",
      "title": "Account Status",
      "emptyText": "-"
    },
    "isCurrent": {
      "type": "boolean"
    },
    "personalEmail": {
      "type": "string",
      "title": "Personal Email",
      "formatKey": "EMAIL"
    },
    "phoneNumber": {
      "type": "string",
      "title": "Primary Phone Number (Mobile)"
    },
    "homeNumber": {
      "type": "string",
      "title": "Home Number"
    },
    "gender": {
      "type": "string",
      "title": "Gender"
    },
    "maritialStatus": {
      "type": "string",
      "title": "Marital Status"
    },
    "officeNumber": {
      "type": "string",
      "title": "Work Number"
    },
    "workEmail": {
      "type": "string",
      "title": "Work Email",
      "formatKey": "EMAIL"
    },
    "department": {
      "type": "string",
      "title": "Division/Department"
    },
    "isRetired": {
      "type": "boolean",
      "title": "Retirement Eligibility Status",
      "displayFormat": "status",
      "statusMap": {
        "true": { "tone": "success", "label": "Retirement eligible" },
        "false": { "tone": "error", "label": "Retirement ineligible" }
      }
    },
    "retirementEligibilityDate": {
      "type": "string",
      "title": "Retirement Eligibility Date",
      "formatKey": "DATE_MDY"
    },
    "employmentCity": {
      "type": "string",
      "title": "City"
    },
    "employmentState": {
      "type": "string",
      "title": "State"
    },
    "employmentCountry": {
      "type": "string",
      "title": "Country"
    },
    "employmentZipPostalCode": {
      "type": "string",
      "title": "ZIP / Postal Code"
    }
  }
}
